/* Functional sticky-boundary reveal. No requests, storage or analytics. */
(() => {
  'use strict';
  if (window.IgneousProductActions) { window.IgneousProductActions.scan(); return; }
  const states = new Map();
  function attach(slot) {
    const inner = slot.closest('.product__inner');
    const container = inner?.closest('.product__main__content');
    if (!inner || !container) return null;
    let observer = null;
    let observationRevision = 0;
    let resizeObserver = null;
    let frame = 0;
    let focusFrame = 0;
    let disposed = false;
    let desired = true;
    function show(value) {
      desired = value;
      const visible = value || slot.contains(document.activeElement);
      slot.classList.toggle('is-revealed', visible);
      if (visible) {
        slot.removeAttribute('inert');
        slot.removeAttribute('aria-hidden');
      } else {
        slot.setAttribute('inert', '');
        slot.setAttribute('aria-hidden', 'true');
      }
    }
    function fallback() {
      observationRevision++;
      observer?.disconnect();
      observer = null;
      slot.removeAttribute('data-reveal-managed');
      show(true);
    }
    function measure() {
      frame = 0;
      if (disposed || !slot.isConnected) return;
      const style = getComputedStyle(inner);
      const parentStyle = getComputedStyle(container);
      const top = Number.parseFloat(style.top);
      const height = inner.getBoundingClientRect().height;
      const parentBox = container.getBoundingClientRect();
      const bottomInset = (Number.parseFloat(parentStyle.paddingBottom) || 0) + (Number.parseFloat(parentStyle.borderBottomWidth) || 0);
      const mobileBreakpoint = Number(window.theme?.sizes?.medium) || 768;
      if (innerWidth < mobileBreakpoint || style.position !== 'sticky' || !Number.isFinite(top) || !height || parentBox.height <= height + bottomInset || typeof IntersectionObserver !== 'function' || typeof ResizeObserver !== 'function') {
        fallback(); return;
      }
      // The native column begins moving up when its containing block's bottom
      // can no longer hold the sticky top offset plus the column's actual height.
      const boundary = top + height + bottomInset;
      const revision = ++observationRevision;
      observer?.disconnect();
      slot.setAttribute('data-reveal-managed', '');
      show(parentBox.bottom < boundary);
      // Observe the whole container, with the root extended below the page.
      // It stays intersecting until the bottom crosses this exact boundary,
      // including jump scrolls; no scroll listener or per-scroll layout read.
      const extension = Math.max(document.documentElement.scrollHeight, innerHeight, 1);
      observer = new IntersectionObserver(entries => {
        for (const entry of entries) {
          if (entry.target === container && !disposed && revision === observationRevision) show(entry.boundingClientRect.bottom < boundary);
        }
      }, {root: null, rootMargin: `${-boundary}px 0px ${extension}px 0px`, threshold: 0});
      observer.observe(container);
    }
    function schedule() {
      if (!disposed && !frame) frame = requestAnimationFrame(measure);
    }
    function focusOut() {
      if (focusFrame) cancelAnimationFrame(focusFrame);
      focusFrame = requestAnimationFrame(() => { focusFrame = 0; if (!disposed) show(desired); });
    }
    slot.addEventListener('focusout', focusOut);
    window.addEventListener('resize', schedule);
    if (typeof ResizeObserver === 'function') {
      resizeObserver = new ResizeObserver(schedule);
      resizeObserver.observe(inner);
      resizeObserver.observe(container);
    }
    measure();
    return {
      refresh: schedule,
      destroy() {
        disposed = true;
        observationRevision++;
        observer?.disconnect();
        resizeObserver?.disconnect();
        if (frame) cancelAnimationFrame(frame);
        if (focusFrame) cancelAnimationFrame(focusFrame);
        slot.removeEventListener('focusout', focusOut);
        window.removeEventListener('resize', schedule);
        slot.removeAttribute('data-reveal-managed');
        slot.removeAttribute('inert');
        slot.removeAttribute('aria-hidden');
        slot.classList.remove('is-revealed');
      }
    };
  }
  function scan() {
    for (const [slot, state] of states) {
      if (!slot.isConnected) { state.destroy(); states.delete(slot); }
    }
    document.querySelectorAll('[data-igneous-secondary-reveal]').forEach(slot => {
      if (states.has(slot)) { states.get(slot).refresh(); return; }
      const state = attach(slot);
      if (state) states.set(slot, state);
    });
  }
  window.IgneousProductActions = {scan};
  scan();
  document.addEventListener('DOMContentLoaded', scan, {once: true});
  ['theme:section:load', 'theme:section:update', 'pageshow'].forEach(name => window.addEventListener(name, scan));
  document.addEventListener('shopify:section:load', scan);
  window.addEventListener('pagehide', () => { states.forEach(state => state.destroy()); states.clear(); });
  document.fonts?.ready.then(scan);
})();
